import { create } from "zustand";
import { api, ApiError, request, setTokenSource } from "./api";
import { native } from "./native";
import type { AuthResult, Me } from "./types";

/**
 * 로그인 상태. 사람은 이메일 로그인 + httpOnly 리프레시 쿠키, 브릿지(Electron)는 머신 계정으로
 * 네이티브 API 가 받아 준 15분 액세스 토큰만 쓴다. 토큰은 메모리에만 둔다.
 */
type Status = "booting" | "anonymous" | "signed-in" | "bridge-error";
type AuthState = {
  status: Status;
  me: Me | null;
  token: string | null;
  expiresAt: number;
  bridgeError: "revoked" | "offline" | "unpaired" | null;
  boot: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setMe: (me: Me) => void;
};

let refreshTimer: ReturnType<typeof setTimeout> | undefined;
/** 로그인 주체가 바뀌면 (로그인·로그아웃·다른 계정) 화면 캐시를 비운다. 이전 사용자의 데이터가 남지 않게. */
let onPrincipalChange: () => void = () => {};
export function setPrincipalChangeHandler(fn: () => void): void {
  onPrincipalChange = fn;
}
let renewing: Promise<string | null> | null = null;

export const useAuth = create<AuthState>((set, get) => {
  const accept = (r: { accessToken: string; expiresIn: number }, me?: Me) => {
    if (me && me.accountId !== get().me?.accountId) onPrincipalChange();
    set({ token: r.accessToken, expiresAt: Date.now() + r.expiresIn * 1000, status: "signed-in", bridgeError: null, ...(me ? { me } : {}) });
    clearTimeout(refreshTimer);
    // 만료 1분 전에 미리 갱신한다.
    refreshTimer = setTimeout(() => void renew(), Math.max(10_000, (r.expiresIn - 60) * 1000));
  };

  /** 액세스 토큰 갱신. 동시에 여러 요청이 401 을 받아도 한 번만 한다. */
  const renew = (): Promise<string | null> => {
    renewing ??= (async () => {
      try {
        const n = native();
        if (n) {
          const s = await n.getUiSession();
          if ("error" in s) {
            set({ status: "bridge-error", bridgeError: s.error });
            return null;
          }
          accept(s);
          return s.accessToken;
        }
        const r = (await request<AuthResult>("POST", "/auth/refresh", {}, { auth: false })).result;
        accept(r, r.me);
        return r.accessToken;
      } catch (e) {
        if (!native() && e instanceof ApiError && e.status === 401) {
          set({ status: "anonymous", me: null, token: null });
          onPrincipalChange();
        }
        return null;
      } finally {
        renewing = null;
      }
    })();
    return renewing;
  };
  setTokenSource({ get: () => get().token, renew });

  return {
    status: "booting",
    me: null,
    token: null,
    expiresAt: 0,
    bridgeError: null,
    boot: async () => {
      if (native()) {
        const t = await renew();
        if (!t) return;
        try {
          const me = await api.get<Me>("/auth/me");
          if (me.accountId !== get().me?.accountId) onPrincipalChange();
          set({ me });
        } catch {
          set({ status: "bridge-error", bridgeError: "offline" });
        }
        return;
      }
      const t = await renew();
      if (!t) set({ status: "anonymous" });
    },
    login: async (email, password) => {
      const r = (await request<AuthResult>("POST", "/auth/login", { email, password, deviceLabel: deviceLabel() }, { auth: false })).result;
      accept(r, r.me);
    },
    logout: async () => {
      try {
        await api.post("/auth/logout");
      } catch {
        /* 이미 끊긴 세션 */
      }
      clearTimeout(refreshTimer);
      set({ status: "anonymous", me: null, token: null });
      onPrincipalChange();
    },
    setMe: (me) => set({ me }),
  };
});

function deviceLabel(): string {
  const ua = navigator.userAgent;
  const os = /Windows/.test(ua) ? "Windows" : /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Mac/.test(ua) ? "Mac" : "기타";
  const browser = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : /Firefox\//.test(ua) ? "Firefox" : "브라우저";
  return `${os} / ${browser}`;
}

